<script setup lang="ts">
import { createError, useRoute } from "#imports";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import AsmaSection from "@/components/asma/AsmaSection.vue";
import CalendarSection from "@/components/calendar/CalendarSection.vue";
import DuaSection from "@/components/dua/DuaSection.vue";
import FitranaSection from "@/components/fitrana/FitranaSection.vue";
import LailatSection from "@/components/lailat/LailatSection.vue";
import EidCelebrationSection from "@/components/home/EidCelebrationSection.vue";
import RamadanStartSection from "@/components/ramadan/RamadanStartSection.vue";
import RozaCountdownSection from "@/components/roza-countdown/RozaCountdownSection.vue";
import RozaSection from "@/components/roza/RozaSection.vue";
import TimeSection from "@/components/time/TimeSection.vue";
import ZakatSection from "@/components/zakat/ZakatSection.vue";
import ZikrSection from "@/components/zikr/ZikrSection.vue";
import { cities } from "@/data/cities";
import type { Component } from "vue";

const { t } = useI18n();
const route = useRoute();
const { isActive: isEidActive } = useEidWindow();

/**
 * Type-safe chip configuration
 */
type ChipKey =
  | "time"
  | "calendar"
  | "roza-countdown"
  | "zikr"
  | "asma"
  | "roza"
  | "zakat"
  | "dua"
  | "lailat"
  | "fitrana"
  | "ramadan-start";

interface ChipConfig {
  readonly key: ChipKey;
  readonly labelKey: string; // Store translation key instead of function
}

const chips: readonly ChipConfig[] = [
  { key: "time", labelKey: "chips.time.label" },
  { key: "roza-countdown", labelKey: "chips.rozaCountdown.label" },
  { key: "calendar", labelKey: "chips.calendar.label" },
  { key: "roza", labelKey: "chips.roza.label" },
  { key: "dua", labelKey: "chips.dua.label" },
  { key: "zikr", labelKey: "chips.zikr.label" },
  { key: "asma", labelKey: "chips.asma.label" },
  { key: "zakat", labelKey: "chips.zakat.label" },
  { key: "lailat", labelKey: "chips.lailat.label" },
  { key: "fitrana", labelKey: "chips.fitrana.label" },
  { key: "ramadan-start", labelKey: "chips.ramadanStart.label" },
] as const;

/**
 * Active section state (defaults to time)
 */
const activeChip = ref<ChipKey>("time");

/**
 * Component registry with type safety
 */
const chipComponentMap: Readonly<Record<ChipKey, Component>> = {
  time: TimeSection,
  "roza-countdown": RozaCountdownSection,
  calendar: CalendarSection,
  zikr: ZikrSection,
  asma: AsmaSection,
  roza: RozaSection,
  zakat: ZakatSection,
  dua: DuaSection,
  lailat: LailatSection,
  fitrana: FitranaSection,
  "ramadan-start": RamadanStartSection,
} as const;

/**
 * Resolved active component (guaranteed fallback)
 */
const ActiveComponent = computed<Component>(
  () => {
    const segments = resolvePathSegments();
    if (segments.length === 0 && isEidActive.value) {
      return EidCelebrationSection;
    }

    return chipComponentMap[activeChip.value];
  },
);

/**
 * Validate and normalize route slug
 */
function isValidChipKey(slug: string): slug is ChipKey {
  return slug in chipComponentMap;
}

function isSitemapRoute(path: string) {
  return path === "/sitemap" || path === "/sitemap.xml";
}

function normalizeSlug(value: string) {
  return value.trim().replace(/^\/+|\/+$/g, "");
}

function resolvePathSegments() {
  const paramSlug = route.params.slug;
  if (Array.isArray(paramSlug)) {
    return paramSlug.map((s) => normalizeSlug(s)).filter(Boolean);
  }
  if (typeof paramSlug === "string" && paramSlug.trim()) {
    return [normalizeSlug(paramSlug)];
  }
  return route.path.split("/").map((s) => normalizeSlug(s)).filter(Boolean);
}

function isValidCatchAllRoute() {
  if (isSitemapRoute(route.path)) return true;

  const segments = resolvePathSegments();
  if (segments.length === 0) return true;
  if (segments.length > 1) return false;

  const slug = segments[0];
  return isValidChipKey(slug) || slug in cities;
}

function resolveActiveChipKey(): ChipKey {
  if (isSitemapRoute(route.path)) {
    return activeChip.value;
  }

  const segments = resolvePathSegments();
  if (segments.length === 0) {
    return "time";
  }

  const slug = segments[0];
  return isValidChipKey(slug) ? slug : "time";
}

/**
 * Sync active chip from route params/path
 * Handles direct URL entry, trailing slash variants, and browser navigation
 */
watch(
  [() => route.params.slug, () => route.path],
  () => {
    if (!isValidCatchAllRoute()) {
      throw createError({
        statusCode: 404,
        statusMessage: "Page not found",
        fatal: true,
      });
    }
    activeChip.value = resolveActiveChipKey();
  },
  { immediate: true },
);
</script>

<template>
  <div
    class="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
    <!-- Dynamic content section -->
    <section class="px-4 py-6" aria-label="Main content">
      <component :is="ActiveComponent" />
    </section>
  </div>
</template>

<style scoped>
/* Cross-browser scrollbar hiding for chip navigation */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none; /* IE/Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
